import React, {createContext, useEffect, useState, useContext} from 'react';
import {
    onAuthStateChanged,
    signInWithEmailAndPassword,
    createUserWithEmailAndPassword,
    sendEmailVerification,
    signOut,
    GoogleAuthProvider,
    signInWithPopup,
    User,
    getIdTokenResult,
    getAuth,
    updatePassword,
    sendPasswordResetEmail
} from 'firebase/auth';
import {auth, functions} from '@/config/firebase';
import {httpsCallable} from "firebase/functions";

interface AuthContextProps {
    user: User | null;
    role: string | null;
    claims: Record<string, any> | null,
    loadingAuth: boolean;
    isUpdatingClaims: boolean;
    loginLoading: boolean;
    googleLoading: boolean;
    registerLoading: boolean;
    login: (email: string, password: string) => Promise<void>;
    loginWithGoogle: () => Promise<void>;
    register: (email: string, password: string, displayName: string, gender: string, birthDate: string) => Promise<void>;
    logout: () => Promise<void>;
    requiresProfileCompletion: boolean,
    updateUserClaims: (gender: string, birthDate: string, displayName?: string) => Promise<void>;
    fetchUserRole: (user: User) => Promise<string>;
    setRequiresProfileCompletion: (value: boolean) => void;
    changePassword: (value: string) => void
    resetPassword: (email: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextProps>({
    user: null,
    role: null,
    claims: null,
    loadingAuth: true,
    isUpdatingClaims: false,
    loginLoading: false,
    googleLoading: false,
    registerLoading: false,
    requiresProfileCompletion: false,
    login: async () => {
    },
    loginWithGoogle: async () => {
    },
    register: async () => {
    },
    logout: async () => {
    },
    updateUserClaims: async () => {
    },
    fetchUserRole: async () => Promise.resolve("user"),
    setRequiresProfileCompletion: () => {
    },
    changePassword: () => {
    },
    resetPassword: async () => {
    },
});


export const AuthProvider = ({children}: { children: React.ReactNode }) => {
    const [user, setUser] = useState<User | null>(null);
    const [role, setRole] = useState<string | null>(null);
    const [loadingAuth, setLoadingAuth] = useState(true);
    const [requiresProfileCompletion, setRequiresProfileCompletion] = useState(false);
    const [claims, setClaims] = useState<Record<string, any> | null>(null);
    const [loginLoading, setLoginLoading] = useState(false);
    const [googleLoading, setGoogleLoading] = useState(false);
    const [registerLoading, setRegisterLoading] = useState(false);
    const [isUpdatingClaims, setIsUpdatingClaims] = useState(false);

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
            if (!currentUser || !currentUser.emailVerified) {
                setUser(null);
                setRole(null);
                setClaims(null);
                setRequiresProfileCompletion(false);
                setLoadingAuth(false);
                return;
            }

            setUser(currentUser);
            await fetchUserRole(currentUser)
            const tokenResult = await getIdTokenResult(currentUser);
            const role = tokenResult.claims.role as string | undefined;

            setRole(role ?? "user");
            setClaims(tokenResult.claims);

            if (!tokenResult.claims.gender || !tokenResult.claims.birthDate) {
                setRequiresProfileCompletion(true);
            } else {
                setRequiresProfileCompletion(false);
            }

            setLoadingAuth(false);
        });

        return () => unsubscribe();
    }, []);


    const fetchUserRole = async (user: User): Promise<string> => {
        try {
            await user.getIdToken(true);
            const tokenResult = await getIdTokenResult(user);
            const role = tokenResult.claims.role as string | undefined;
            setClaims(tokenResult.claims);
            return role ?? "user";
        } catch (error) {
            console.error("Ошибка при получении роли:", error);
            return "user";
        }
    };

    const changePassword = async (newPassword: string) => {
        try {
            const auth = getAuth();
            const user = auth.currentUser;

            if (!user) {
                throw new Error("User is not logged in");
            }

            await updatePassword(user, newPassword);
        } catch (error) {
            console.error("Error updating password:", (error as Error).message);
        }

    };

    const updateUserClaims = async (gender: string, birthDate: string, displayName?: string) => {
        if (!auth.currentUser) {
            throw new Error("Пользователь не авторизован");
        }
        setIsUpdatingClaims(true);

        try {
            const setCustomClaims = httpsCallable(functions, "setCustomClaims");

            const claimsData: Record<string, string> = {
                uid: auth.currentUser.uid,
                gender,
                birthDate,
                role: role ? role : 'user'
            };

            if (displayName) {
                claimsData.displayName = displayName;
            }

            await setCustomClaims(claimsData);
            await auth.currentUser.getIdToken(true);
            await fetchUserRole(auth.currentUser);
        } catch (error) {
            console.error("Ошибка при обновлении пользовательских данных:", error);
            throw new Error("Не удалось обновить данные. Попробуйте снова.");
        } finally {
            setIsUpdatingClaims(false);
        }
    };

    const login = async (email: string, password: string) => {
        setLoginLoading(true);
        try {
            const userCredential = await signInWithEmailAndPassword(auth, email, password);
            if (!userCredential.user.emailVerified) {
                await signOut(auth);
                throw new Error('Ваш адрес электронной почты не подтверждён. Проверьте почту и повторите вход.');
            }
            const fetchedRole = await fetchUserRole(userCredential.user);
            setRole(fetchedRole);
        } finally {
            setLoginLoading(false);
        }
    };

    const loginWithGoogle = async () => {
        setGoogleLoading(true);
        try {
            const provider = new GoogleAuthProvider();
            const userCredential = await signInWithPopup(auth, provider);
            const fetchedRole = await fetchUserRole(userCredential.user);
            setRole(fetchedRole);
        } finally {
            setGoogleLoading(false);
        }
    };


    const register = async (email: string, password: string, displayName: string, gender: string, birthDate: string) => {
        setRegisterLoading(true);
        try {
            const userCredential = await createUserWithEmailAndPassword(auth, email, password);
            const user = userCredential.user;

            const setCustomClaims = httpsCallable(functions, "setCustomClaims");
            await setCustomClaims({
                uid: user.uid,
                displayName,
                gender,
                birthDate,
            });

            setRequiresProfileCompletion(false);
            await sendEmailVerification(user);
            await signOut(auth);
        } finally {
            setRegisterLoading(false);
        }
    };

    const resetPassword = async (email: string) => {
        await sendPasswordResetEmail(auth, email);
    };

    const logout = async () => {
        await signOut(auth);
        setRole(null);
    };

    const value: AuthContextProps = {
        user,
        role,
        loadingAuth,
        loginLoading,
        googleLoading,
        registerLoading,
        requiresProfileCompletion,
        login,
        loginWithGoogle,
        register,
        logout,
        updateUserClaims,
        fetchUserRole,
        setRequiresProfileCompletion,
        changePassword,
        claims,
        resetPassword,
        isUpdatingClaims
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
